import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
} from "@mui/material";
import { toWords } from "number-to-words";
import { Field, Formik, Form } from "formik";
import { getInvoiceById } from "../../../../../service/Invoice/invoice";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { useReactToPrint } from "react-to-print";
import DownloadReturn from "../../../../Return/ViewReturnInvoice/DownloadReturn/DownloadReturn";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ImageUpload from "../../../../../components/Form/ImageUpload";
import dayjs from "dayjs";

const ViewInvoicePopup = ({ open, close, invoiceId }) => {
    const [invoiceData, setInvoiceData] = useState<any>({});
    const contentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({ contentRef });

    useEffect(() => {
        (async () => {
            try {
                const responseInvoiceData = await getInvoiceById(invoiceId);
                if (responseInvoiceData.status === 200) {
                    setInvoiceData(responseInvoiceData.data.data);
                }
            } catch (error) { }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rows = invoiceData?.items || [];
    const totalAmount = parseFloat(invoiceData.total_amount || 0).toFixed(2);
    const billing = invoiceData?.customer?.billing_address || {};
    const shipping = invoiceData?.shipping || {};
    const customer = invoiceData?.customer || {};
    const totalQty = rows.reduce((acc, item) => acc + item.quantity, 0);

    const totalAmountInWords = Number(invoiceData?.total_amount || 0);
    const amountInWords = toWords(totalAmountInWords)
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()) + " Rupees Only";

    return (
        <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
            <Box
                display="flex"
                justifyContent="flex-end"
                position="sticky"
                top={0}
                zIndex={10}
                p={2}
                borderBottom="1px solid #ccc"
            >
                <Box display="flex" gap={1}>
                    {invoiceData && (
                        <PDFDownloadLink
                            document={<DownloadReturn data={invoiceData} />}
                            fileName={`${new Date()
                                .toISOString()
                                .slice(0, 10)
                                .replace(/-/g, "")}-invoice.pdf`}
                        >
                            <IconButton>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </PDFDownloadLink>
                    )}
                    <IconButton onClick={handlePrint}>
                        <PrintIcon />
                    </IconButton>
                    <Button variant="outlined" onClick={close} size="small">
                        Close
                    </Button>
                </Box>
            </Box>
            <DialogContent>
                <Box ref={contentRef} border="1px solid #ccc" minHeight={700} sx={{ backgroundColor: 'white' }}>
                    <Grid container alignItems="center" justifyContent="space-between" p={2}>
                        <Grid item xs={6}>
                            <Typography variant="h6">Tax Invoice</Typography>
                            <Typography variant="body2" mt={.7}>
                                <strong>GSTIN : </strong>{invoiceData?.store?.gst_no}
                            </Typography>
                        </Grid>

                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <Formik
                                enableReinitialize
                                initialValues={{
                                    storeImage: invoiceData?.store?.logo || null,
                                }}
                                onSubmit={() => { }}
                            >
                                {() => (
                                    <Form>
                                        <Field
                                            name="storeImage"
                                            component={ImageUpload}
                                            caption=""
                                            isEditMode
                                        />
                                        <Box mt={.4} textAlign="center">
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {invoiceData?.store?.name}
                                            </Typography>
                                        </Box>

                                    </Form>
                                )}
                            </Formik>
                        </Grid>
                    </Grid>

                    <Table
                        size="small"
                        sx={{
                            mb: 2,
                            borderTop: "1px solid #ccc",
                            borderBottom: "1px solid #ccc",
                            '& td': {
                                padding: "8px"
                            }
                        }}
                    >
                        <TableBody>
                            <TableRow sx={{ borderBottom: "1px solid #ccc" }}>
                                <TableCell sx={{ width: "50%", borderBottom: "1px solid #ccc" }}>
                                    <strong>Invoice No :</strong> {invoiceData.id}
                                </TableCell>
                                <TableCell sx={{ width: "50%", borderLeft: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
                                    <strong>Order No :</strong> {invoiceData?.order_id || ""}
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ borderBottom: "1px solid #ccc" }}>
                                <TableCell sx={{ width: "50%", borderBottom: "1px solid #ccc" }}>
                                    <strong>Invoice Date :</strong>{" "}
                                    {invoiceData.invoice_date ? dayjs(invoiceData.invoice_date).format("DD/MM/YYYY") : ""}
                                </TableCell>
                                <TableCell sx={{ width: "50%", borderBottom: "1px solid #ccc", borderLeft: "1px solid #ccc" }}>
                                    <strong>Order Date :</strong>
                                    {invoiceData.order_date ? dayjs(invoiceData.order_date).format("DD/MM/YYYY") : ""}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: "50%", borderBottom: "1px solid #ccc" }}>
                                    <strong>Fssai No :</strong> {invoiceData?.store?.fssai_no || ""}
                                </TableCell>
                                <TableCell sx={{ width: "50%", borderLeft: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
                                    <strong>GST Inclusive : </strong> {invoiceData?.store?.gst_inclusive === true ? "Yes" : "No"}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Table
                        size="small"
                        sx={{
                            border: "1px solid #ccc",
                            borderCollapse: "collapse",
                            width: '100%',
                            '& th, & td': {
                                border: '1px solid #ccc',
                                padding: '8px',
                                verticalAlign: 'top'
                            },
                            '& th': {
                                backgroundColor: '#f9f9f9',
                                textAlign: 'center',
                                fontWeight: 'bold'
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>Billing Address</TableCell>
                                <TableCell>Delivery Address</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Typography fontWeight="bold">{customer.name}</Typography>
                                    <Typography>{billing.address1}, {billing.address2}</Typography>
                                    <Typography>{billing.address3}, {billing.city}</Typography>
                                    <Typography>State : {billing.state}</Typography>
                                    <Typography>Pincode : {billing.pincode}</Typography>
                                    <Typography>Phone No : {customer.phone}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography fontWeight="bold">{customer.name}</Typography>
                                    <Typography>{shipping.address1}, {shipping.address2}</Typography>
                                    <Typography>{shipping.address3}, {shipping.city}</Typography>
                                    <Typography>State : {shipping.state}</Typography>
                                    <Typography>Pincode: {shipping.pincode}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <TableContainer>
                        <Table sx={{ marginTop: 2 }}>
                            <TableHead>
                                <TableRow >
                                    {["S.No.", "Product", "Quantity", "MRP (₹)", "Selling Price (₹)", "Total Amount (₹)"].map((header, i) => (
                                        <TableCell
                                            key={i}
                                            sx={{
                                                borderTop: '1px solid #ccc',
                                                borderBottom: '1px solid #ccc',
                                                borderLeft: i === 0 ? '' : '1px solid #ccc',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((item, index) => (
                                    <TableRow key={item.id} >
                                        <TableCell sx={{ borderBottom: '1px solid #ccc' }}>{index + 1}</TableCell>
                                        <TableCell sx={{ borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>{item.product_name}</TableCell>
                                        <TableCell sx={{ borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>{item.quantity}</TableCell>
                                        <TableCell sx={{ borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>{parseFloat(item.original_price).toFixed(2)}</TableCell>
                                        <TableCell sx={{ borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>{parseFloat(item.price).toFixed(2)}</TableCell>
                                        <TableCell sx={{ borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                                            {(item.quantity * item.price).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                                        <strong>Total</strong>
                                    </TableCell>
                                    <TableCell sx={{ borderTop: '1px solid #ccc', borderLeft: '1px solid #ccc', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                                        {totalQty}
                                    </TableCell>
                                    <TableCell colSpan={2} sx={{ borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}></TableCell>
                                    <TableCell sx={{ borderTop: '1px solid #ccc', borderLeft: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                                        {totalAmount}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2 }}>
                        <Table sx={{ width: '100%' }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            verticalAlign: 'top',
                                            width: '50%',
                                            borderBottom: '1px solid #ccc',
                                            borderTop: '1px solid #ccc',
                                            padding: 1,
                                        }}
                                    >
                                        <Grid container>
                                            <Grid item xs={6} paddingY={1}>
                                                <Typography fontWeight="bold">Payment Mode </Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="left" paddingY={1}>
                                                <Typography>{invoiceData?.payment_method || ""}</Typography>
                                            </Grid>
                                            <Grid item xs={6} paddingY={1}>
                                                <Typography fontWeight="bold">Status </Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="left" paddingY={1}>
                                                <Typography>{invoiceData?.status || ""}</Typography>
                                            </Grid>
                                            {invoiceData?.cancel_reason && (
                                                <>
                                                    <Grid item xs={6} paddingY={1}>
                                                        <Typography fontWeight="bold">Cancel Reason</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} textAlign="left" paddingY={1}>
                                                        <Typography>{invoiceData.cancel_reason.reason || ""}</Typography>
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </TableCell>

                                    <TableCell colSpan={2} sx={{ borderTop: '1px solid #ccc', borderLeft: '1px solid #ccc', padding: 0 }}>
                                        <Grid container>
                                            {[
                                                { label: "Subtotal", value: invoiceData.sub_total },
                                                { label: "CGST", value: invoiceData.cgst },
                                                { label: "SGST", value: invoiceData.sgst },
                                                { label: "Delivery Charge", value: invoiceData.shipping_charge },
                                                { label: "Discount", value: "", isNegative: true }
                                            ].map((item, index) => (
                                                <Grid
                                                    container
                                                    key={item.label}
                                                    sx={{
                                                        borderBottom: index !== 6 ? '1px solid #ccc' : 'none',
                                                        paddingY: 1,
                                                        paddingX: 2
                                                    }}
                                                >
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {item.label}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} textAlign="right">
                                                        <Typography variant="body2">
                                                            {item.isNegative ? "" : ""}
                                                            ₹ {parseFloat(item.value || 0).toFixed(2)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell
                                        sx={{
                                            verticalAlign: 'top',
                                            borderBottom: '1px solid #ccc',
                                            borderTop: '1px solid #ccc',
                                            padding: 0,
                                        }}
                                    >
                                        <Grid container sx={{ height: '100%' }}>
                                            <Grid
                                                item
                                                xs={6}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: 1,
                                                }}
                                            >
                                                <Typography fontWeight="bold">
                                                    Total Amount In Words
                                                </Typography>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                sx={{
                                                    borderLeft: '1px solid #ccc', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'flex-end', padding: 1,
                                                }}
                                            >
                                                {amountInWords}
                                            </Grid>
                                        </Grid>
                                    </TableCell>

                                    <TableCell colSpan={2} sx={{ borderBottom: '1px solid #ccc', borderLeft: '1px solid #ccc' }}>
                                        <Grid container>
                                            <Grid item xs={6}>
                                                <Typography><strong>TOTAL</strong></Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                ₹{parseFloat(invoiceData.total_amount || 0).toFixed(2)}
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>

                    <Box display="flex" justifyContent="space-between" width="100%" minHeight={80} px={1}>
                        <Box display="flex" alignItems="flex-start">
                            <Typography variant="caption">E. & O.E.</Typography>
                        </Box>

                        <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="flex-end">
                            <Typography variant="caption">For</Typography>
                            <Typography variant="caption">
                                This is a computer-generated invoice and does not require a signature.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ViewInvoicePopup;